import type { Task } from '@/tasks/task';
/**
 * 種別ごとの色。
 *
 * 一覧を流し読みするとき「どの種類の仕事か」が最初に目に入るようにする。彩度は抑え、
 * 既知の種別だけに色を割り当てる（未知の種別はニュートラルに倒す）。
 * @graph-connects none
 */
const KIND_COLOR: Record<string, string> = {
  月次経理: 'var(--color-kind-monthly)',
  決算: 'var(--color-kind-closing)',
  会議: 'var(--color-kind-meeting)',
  その他タスク: 'var(--color-kind-other)',
};

/**
 * 種別ごとの文字色。
 *
 * 面に使うパステルをそのまま文字にすると白地で読めないため、同系の濃い色を別に持つ。
 * @graph-connects none
 */
const KIND_TEXT: Record<string, string> = {
  月次経理: 'var(--color-kind-monthly-text)',
  決算: 'var(--color-kind-closing-text)',
  会議: 'var(--color-kind-meeting-text)',
  その他タスク: 'var(--color-kind-other-text)',
};

/**
 * 種別ごとの色（16 進）。
 *
 * 画面の描画は CSS 変数（`kindColor`）を使うが、設定画面の色ピッカーは具体的な値を
 * 必要とするため、同じ色をここにも持つ。styles.css の `--color-kind-*` と揃えること。
 * @graph-connects none
 */
const KIND_HEX: Record<string, string> = {
  月次経理: '#d88fb4',
  決算: '#e7a184',
  会議: '#9ecfa6',
  その他タスク: '#b9afb9',
};

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別の色を 16 進で返す。未知の種別はニュートラルな色に倒す
 * @graph-connects none
 */
export function kindHex(kind: string): string {
  return KIND_HEX[kind] ?? '#b9afb9';
}

/** 種別ごとの淡い背景 */
/** @graph-connects none */
const KIND_BG: Record<string, string> = {
  月次経理: 'var(--color-kind-monthly-bg)',
  決算: 'var(--color-kind-closing-bg)',
  会議: 'var(--color-kind-meeting-bg)',
  その他タスク: 'var(--color-kind-other-bg)',
};

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別の淡い背景色を返す。未知の種別は面を持たせない
 * @graph-connects none
 */
export function kindBackground(kind: string): string {
  return KIND_BG[kind] ?? 'transparent';
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別の色（面に使うパステル）を返す。未知の種別はニュートラルな色に倒す
 * @graph-connects none
 */
export function kindColor(kind: string): string {
  return KIND_COLOR[kind] ?? 'var(--color-text-muted)';
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別の文字色を返す。面のパステルは文字には薄すぎるため、こちらを使う
 * @graph-connects none
 */
export function kindTextColor(kind: string): string {
  return KIND_TEXT[kind] ?? 'var(--color-text-secondary)';
}

/**
 * KindLabel の props。
 */
export interface KindLabelProps {
  /** 表示する種別 */
  kind: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別をチップで示す。面は淡い背景、文字は読める濃さの同系色にする
 * @graph-connects kindTextColor [calls] 種別の文字色を引く
 */
export function KindLabel({ kind }: KindLabelProps) {
  return (
    <span
      className="inline-block rounded px-1.5 py-0.5 text-xs whitespace-nowrap"
      style={{ backgroundColor: kindBackground(kind), color: kindTextColor(kind) }}
    >
      {kind}
    </span>
  );
}

/**
 * カレンダーのチップの、状況ごとの文字色と枠。
 *
 * 面の色は種別から取る（`chipStyle`）。白い面だとカレンダーの地と同化して、
 * どこまでが 1 件なのか分からなくなるため
 * @graph-connects none
 */
export const CHIP_CLASS = {
  未着手: 'border border-[var(--color-border-subtle)] text-[var(--color-text-primary)]',
  対応中: 'border border-[var(--color-doing-border)] text-[var(--color-doing-text)]',
  対応済: 'border border-[var(--color-border-subtle)] text-[var(--color-done-text)]',
} as const;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business チップの面と左の縦線の色を決める。済んだものは面を落として、残っているものが前に出るようにする
 * @graph-connects kindBackground [calls] 種別の面の色を引く
 * @graph-connects kindColor [calls] 種別の線の色を引く
 */
export function chipStyle(task: Task): { backgroundColor: string; borderLeftColor: string } {
  return {
    backgroundColor: task.status === '対応済' ? 'var(--color-done-bg)' : kindBackground(task.kind),
    borderLeftColor: kindColor(task.kind),
  };
}

/**
 * カレンダーで予定の面を塗る色。Google カレンダーの既定パレットに合わせる。
 * 一覧やバッジの種別色（パステル）とは別に持つ。カレンダーは Google カレンダーと見比べて使うため
 * @graph-connects none
 */
const CALENDAR_COLOR: Record<string, string> = {
  会議: 'var(--color-cal-meeting)',
  月次経理: 'var(--color-cal-monthly)',
  決算: 'var(--color-cal-closing)',
  その他タスク: 'var(--color-cal-other)',
};

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business カレンダーで予定を塗る色を返す。未知の種別はグラファイトに倒す
 * @graph-connects none
 */
export function calendarColor(kind: string): string {
  return CALENDAR_COLOR[kind] ?? 'var(--color-cal-unknown)';
}

/**
 * 予定の面の文字の飾り。対応済は取り消し線、対応中は枠で目立たせる（Google カレンダーには状況が無いので、ここだけ足す）
 * @graph-connects none
 */
export const EVENT_CLASS = {
  未着手: '',
  対応中: 'outline-2 -outline-offset-2 outline-[var(--color-text-primary)]',
  対応済: 'line-through',
} as const;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 予定の面と文字の色を返す。予定は種別のパステルで塗って濃い文字にし、対応済にしたものだけ淡い面と灰色の文字にする（時間が過ぎただけでは薄くしない。終わったかどうかは状況で見る）
 * @graph-connects calendarColor [calls] 種別の色を引く
 */
export function eventStyle(task: Task): { backgroundColor: string; color: string } {
  const color = calendarColor(task.kind);
  if (task.status === '対応済') {
    return {
      backgroundColor: `color-mix(in srgb, ${color} 45%, white)`,
      color: 'var(--color-cal-label)',
    };
  }
  return { backgroundColor: color, color: 'var(--color-event-text)' };
}
